import { useState } from 'react';
import { DynamicWidgetConfig, VisitorInfo, WidgetConfig } from '../types';

interface PreChatFormProps {
  config: WidgetConfig;
  dynamicConfig?: DynamicWidgetConfig | null;
  onSubmit: (visitorInfo: VisitorInfo) => void;
  onClose: () => void;
  isClosing: boolean;
  headerGradient: string;
  windowPosClass: string;
}

// SVG Icons
const UserIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

const MailIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
  </svg>
);

const PhoneIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.362 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.338 1.85.573 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

const ArrowIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

const CloseIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

const HeadphonesIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
    <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
  </svg>
);

export const PreChatForm = ({
  config,
  dynamicConfig,
  onSubmit,
  onClose,
  isClosing,
  headerGradient,
  windowPosClass
}: PreChatFormProps) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const brandColor = dynamicConfig?.brandColor || config.brandColor || '#673AB7';
  const agentDisplayName = dynamicConfig?.agentName || config.agentName || 'Atendimento';
  const agentDisplayAvatar = dynamicConfig?.agentAvatar || config.agentAvatar;
  const welcomeMsg = dynamicConfig?.welcomeMessage || config.welcomeMessage;
  const appName = dynamicConfig?.appName || 'TROIA';
  const rawWebsiteUrl = dynamicConfig?.appWebsiteUrl || '';
  const appWebsiteUrl = rawWebsiteUrl
    ? `${!/^https?:\/\//i.test(rawWebsiteUrl) ? `https://${rawWebsiteUrl}` : rawWebsiteUrl}?utm_source=widget&utm_medium=chat&utm_campaign=powered_by`
    : '';

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!name.trim()) {
      newErrors.name = 'Nome é obrigatório';
    }

    if (!email.trim()) {
      newErrors.email = 'Email é obrigatório';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.email = 'Email inválido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    onSubmit({
      name: name.trim(),
      email: email.trim(),
      phone: phone.trim() || undefined
    });
  };

  return (
    <div className={`tw-window${windowPosClass}${isClosing ? ' tw-window--closing' : ''}`}>
      {/* Header */}
      <div className="tw-header" style={{ background: headerGradient }}>
        <div className="tw-header-info">
          <div className="tw-header-avatar">
            {agentDisplayAvatar ? (
              <img src={agentDisplayAvatar} alt={agentDisplayName} />
            ) : (
              <div className="tw-header-avatar-placeholder">
                <HeadphonesIcon />
              </div>
            )}
          </div>
          <div className="tw-header-text">
            <div className="tw-header-name">
              {agentDisplayName}
            </div>
            <div className="tw-header-subtitle">Antes de iniciar, nos conte sobre você</div>
          </div>
        </div>

        <div className="tw-header-actions">
          <button
            onClick={onClose}
            className="tw-header-btn"
            aria-label="Fechar"
          >
            <CloseIcon />
          </button>
        </div>
      </div>

      {/* Form Body */}
      <form onSubmit={handleSubmit} className="tw-form-body">
        <div className="tw-form-welcome">
          {welcomeMsg || 'Preencha seus dados abaixo para que possamos atendê-lo melhor.'}
        </div>

        {/* Nome */}
        <div className="tw-form-group">
          <label className="tw-form-label">
            Nome<span className="tw-form-required">*</span>
          </label>
          <div className={`tw-form-input-wrapper${errors.name ? ' tw-form-input-wrapper--error' : ''}`}>
            <span className="tw-form-icon"><UserIcon /></span>
            <input
              type="text"
              value={name}
              onChange={(e) => { setName(e.target.value); setErrors(prev => ({ ...prev, name: '' })); }}
              placeholder="Seu nome completo"
              className="tw-form-input"
              autoComplete="name"
            />
          </div>
          {errors.name && (
            <div className="tw-form-error">{errors.name}</div>
          )}
        </div>

        {/* Email */}
        <div className="tw-form-group">
          <label className="tw-form-label">
            Email<span className="tw-form-required">*</span>
          </label>
          <div className={`tw-form-input-wrapper${errors.email ? ' tw-form-input-wrapper--error' : ''}`}>
            <span className="tw-form-icon"><MailIcon /></span>
            <input
              type="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setErrors(prev => ({ ...prev, email: '' })); }}
              placeholder="seu@email.com"
              className="tw-form-input"
              autoComplete="email"
            />
          </div>
          {errors.email && (
            <div className="tw-form-error">{errors.email}</div>
          )}
        </div>

        {/* Telefone */}
        <div className="tw-form-group">
          <label className="tw-form-label">
            Telefone <span style={{ fontWeight: 400, opacity: 0.6 }}>(opcional)</span>
          </label>
          <div className="tw-form-input-wrapper">
            <span className="tw-form-icon"><PhoneIcon /></span>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="(11) 99999-9999"
              className="tw-form-input"
              autoComplete="tel"
            />
          </div>
        </div>

        {/* Submit */}
        <button
          type="submit"
          className="tw-form-submit"
          style={{ backgroundColor: brandColor }}
        >
          Iniciar Conversa
          <ArrowIcon />
        </button>
      </form>

      {/* Powered by */}
      <div className="tw-powered">
        <span className="tw-powered-text">
          Powered by {appWebsiteUrl ? (
            <a href={appWebsiteUrl} target="_blank" rel="noopener noreferrer" className="tw-powered-link">{appName}</a>
          ) : (
            <span className="tw-powered-link">{appName}</span>
          )}
        </span>
      </div>
    </div>
  );
};
